import {
  TrendUp,
  TrendDown,
  Bell,
  ChartLineUp,
  Wallet,
  Star,
  ShieldCheck,
  Sparkle,
  Rocket,
  DotsThree,
} from "@phosphor-icons/react";

/* ---------- Device frame ---------- */
export const Phone = ({ children, className = "", glow = true, testid }) => (
  <div className={`relative ${className}`} data-testid={testid}>
    {glow && (
      <div className="absolute -inset-6 rounded-[3rem] bg-teal-500/10 blur-3xl" aria-hidden="true" />
    )}
    <div className="relative mx-auto w-[290px] rounded-[2.6rem] border border-white/12 bg-[#0b1220] p-2.5 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] ring-1 ring-white/5">
      <div className="relative h-[600px] w-full overflow-hidden rounded-[2.1rem] bg-gradient-to-b from-[#070c16] to-[#050810]">
        <div className="absolute left-1/2 top-2 z-20 h-6 w-28 -translate-x-1/2 rounded-full bg-black/80" />
        <div className="no-scrollbar h-full w-full overflow-hidden">{children}</div>
      </div>
    </div>
  </div>
);

const StatusBar = ({ title }) => (
  <div className="flex items-center justify-between px-5 pt-9 pb-3">
    <span className="font-display text-sm font-bold text-white">{title}</span>
    <DotsThree size={22} className="text-white/50" />
  </div>
);

/* ---------- SVG atoms ---------- */
const AreaChart = ({ color = "#00E5FF", h = 88 }) => (
  <svg viewBox="0 0 240 90" className="h-full w-full" preserveAspectRatio="none" style={{ height: h }}>
    <defs>
      <linearGradient id={`g-${color}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.35" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M0 62 L20 58 L40 66 L60 48 L80 52 L100 38 L120 44 L140 28 L160 34 L180 20 L200 26 L220 14 L240 22 L240 90 L0 90 Z"
      fill={`url(#g-${color})`}
    />
    <path
      d="M0 62 L20 58 L40 66 L60 48 L80 52 L100 38 L120 44 L140 28 L160 34 L180 20 L200 26 L220 14 L240 22"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Donut = ({ value = 82, color = "#00E5FF", label = "AI Score" }) => {
  const r = 34;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative grid place-items-center">
      <svg width="92" height="92" viewBox="0 0 92 92">
        <circle cx="46" cy="46" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
        <circle
          cx="46"
          cy="46"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          transform="rotate(-90 46 46)"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-display text-xl font-extrabold text-white">{value}</span>
        <span className="text-[9px] uppercase tracking-wider text-white/50">{label}</span>
      </div>
    </div>
  );
};

const Coin = ({ sym, color }) => (
  <span
    className="grid h-9 w-9 place-items-center rounded-full text-xs font-bold"
    style={{ background: `${color}22`, color }}
  >
    {sym}
  </span>
);

const Chip = ({ children, tone = "teal" }) => {
  const map = {
    teal: "bg-teal-500/15 text-teal-300 border-teal-500/25",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/25",
    slate: "bg-white/8 text-white/70 border-white/15",
  };
  return (
    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold ${map[tone]}`}>
      {children}
    </span>
  );
};

/* ---------- Screens ---------- */
export const AnalysisScreen = () => (
  <div>
    <StatusBar title="AI Analysis" />
    <div className="space-y-4 px-4 pb-6">
      <div className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-3.5">
        <Coin sym="BTC" color="#F7931A" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Bitcoin</p>
          <p className="text-xs text-white/45">BTC · $64,180</p>
        </div>
        <Chip tone="green">
          <span className="inline-flex items-center gap-1"><TrendUp size={11} weight="bold" />+2.4%</span>
        </Chip>
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
        <div className="flex items-center justify-around">
          <Donut value={82} color="#00E5FF" label="AI Score" />
          <Donut value={34} color="#f59e0b" label="Risk" />
        </div>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-teal-500/20 bg-teal-500/10 px-3.5 py-2.5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-teal-300">
            <Sparkle size={13} weight="fill" /> Recommendation
          </span>
          <span className="font-display text-sm font-bold text-white">Accumulate</span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
        <p className="mb-2 text-xs font-semibold text-white/70">Time-based outlook</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            ["24h", "Neutral", "slate"],
            ["7d", "Positive", "green"],
            ["30d", "Positive", "green"],
          ].map(([t, v, tone]) => (
            <div key={t} className="rounded-xl border border-white/6 bg-white/[0.02] py-2.5">
              <p className="text-[10px] text-white/40">{t}</p>
              <div className="mt-1.5 flex justify-center"><Chip tone={tone}>{v}</Chip></div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-white/50">Price action</span>
          <span className="text-teal-400">1M</span>
        </div>
        <AreaChart color="#00E5FF" h={70} />
      </div>
    </div>
  </div>
);

export const AssetDetailScreen = () => (
  <div>
    <StatusBar title="Ethereum" />
    <div className="space-y-4 px-4 pb-6">
      <div className="rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-4">
        <div className="flex items-center gap-3">
          <Coin sym="ETH" color="#627EEA" />
          <div>
            <p className="font-display text-lg font-bold text-white">$3,412.60</p>
            <p className="inline-flex items-center gap-1 text-xs text-emerald-400">
              <TrendUp size={12} weight="bold" /> +1.8% today
            </p>
          </div>
        </div>
        <div className="mt-3"><AreaChart color="#627EEA" h={92} /></div>
        <div className="mt-2 flex justify-between text-[10px] text-white/40">
          <span>1D</span><span>1W</span><span className="text-teal-400">1M</span><span>3M</span><span>1Y</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          ["Market Cap", "$410.2B"],
          ["24h Volume", "$14.8B"],
          ["Circ. Supply", "120.2M"],
          ["All-time High", "$4,878"],
        ].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
            <p className="text-[10px] text-white/40">{k}</p>
            <p className="mt-1 text-sm font-semibold text-white">{v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-white/70">
            <ShieldCheck size={14} className="text-teal-400" /> Risk breakdown
          </span>
          <Chip tone="amber">Moderate</Chip>
        </div>
        {[
          ["Volatility", 46, "#f59e0b"],
          ["Liquidity", 78, "#00E5FF"],
          ["Momentum", 64, "#34d399"],
        ].map(([k, v, c]) => (
          <div key={k} className="mb-2.5 last:mb-0">
            <div className="mb-1 flex justify-between text-[10px] text-white/50">
              <span>{k}</span><span>{v}/100</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
              <div className="h-full rounded-full" style={{ width: `${v}%`, background: c }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const MonitoringScreen = () => (
  <div>
    <StatusBar title="Smart Monitoring" />
    <div className="space-y-3 px-4 pb-6">
      <div className="rounded-2xl border border-teal-500/20 bg-teal-500/8 p-4">
        <div className="flex items-center gap-2 text-teal-300">
          <span className="relative grid h-8 w-8 place-items-center rounded-full bg-teal-500/20" style={{ animation: "pulse-ring 2.2s infinite" }}>
            <Bell size={16} weight="fill" />
          </span>
          <span className="text-xs font-semibold">3 assets actively monitored</span>
        </div>
      </div>
      {[
        ["SOL", "#14F195", "Solana", "Momentum shift detected", "8m ago", "teal"],
        ["BTC", "#F7931A", "Bitcoin", "Approaching resistance zone", "22m ago", "amber"],
        ["ETH", "#627EEA", "Ethereum", "Volatility within normal range", "1h ago", "slate"],
      ].map(([sym, c, name, msg, time, tone]) => (
        <div key={sym} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-3.5">
          <Coin sym={sym} color={c} />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">{name}</p>
              <span className="text-[10px] text-white/35">{time}</span>
            </div>
            <p className="mt-0.5 text-xs text-white/55">{msg}</p>
            <div className="mt-2"><Chip tone={tone}>Insight</Chip></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PortfolioScreen = () => (
  <div>
    <StatusBar title="Portfolio" />
    <div className="space-y-4 px-4 pb-6">
      <div className="rounded-2xl border border-white/8 bg-gradient-to-br from-teal-500/10 to-transparent p-4">
        <p className="text-xs text-white/50">Tracked balance</p>
        <p className="font-display text-3xl font-extrabold text-white">$24,860.40</p>
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-400">
          <TrendUp size={12} weight="bold" /> +$412.18 (24h)
        </p>
        <div className="mt-3"><AreaChart color="#00E5FF" h={64} /></div>
      </div>
      <div className="space-y-2.5">
        {[
          ["BTC", "#F7931A", "Bitcoin", "0.21", "$13,478", "+2.4%", true],
          ["ETH", "#627EEA", "Ethereum", "2.4", "$8,190", "+1.8%", true],
          ["SOL", "#14F195", "Solana", "18.5", "$3,192", "-0.6%", false],
        ].map(([sym, c, name, amt, val, chg, up]) => (
          <div key={sym} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-3">
            <Coin sym={sym} color={c} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">{name}</p>
              <p className="text-[11px] text-white/40">{amt} {sym}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-white">{val}</p>
              <p className={`text-[11px] ${up ? "text-emerald-400" : "text-rose-400"}`}>{chg}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const WatchlistScreen = () => (
  <div>
    <StatusBar title="Watchlist" />
    <div className="space-y-2.5 px-4 pb-6 pt-1">
      {[
        ["BTC", "#F7931A", "Bitcoin", "$64,180", "+2.4%", true],
        ["ETH", "#627EEA", "Ethereum", "$3,412", "+1.8%", true],
        ["SOL", "#14F195", "Solana", "$172.4", "-0.6%", false],
        ["AVAX", "#E84142", "Avalanche", "$38.20", "+3.1%", true],
        ["LINK", "#2A5ADA", "Chainlink", "$16.85", "+0.9%", true],
        ["DOT", "#E6007A", "Polkadot", "$7.12", "-1.2%", false],
      ].map(([sym, c, name, price, chg, up]) => (
        <div key={sym} className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-3">
          <Star size={16} weight="fill" className="text-teal-400" />
          <Coin sym={sym} color={c} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">{name}</p>
            <p className="text-[11px] text-white/40">{sym}</p>
          </div>
          <div className="w-16"><AreaChart color={up ? "#34d399" : "#fb7185"} h={26} /></div>
          <div className="text-right">
            <p className="text-xs font-semibold text-white">{price}</p>
            <p className={`text-[11px] ${up ? "text-emerald-400" : "text-rose-400"}`}>{chg}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PresaleScreen = () => (
  <div>
    <StatusBar title="Presale Intelligence" />
    <div className="space-y-4 px-4 pb-6">
      <div className="rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-4">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-white">
            <Rocket size={16} className="text-teal-400" weight="fill" /> Project Review
          </span>
          <Chip tone="teal">Analyzed</Chip>
        </div>
        <div className="mt-4 flex items-center justify-around">
          <Donut value={71} color="#00E5FF" label="AI Score" />
          <Donut value={52} color="#f59e0b" label="Risk" />
        </div>
      </div>
      <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4 space-y-2.5">
        {[
          ["Tokenomics", "Reviewed", "green"],
          ["Liquidity signals", "Moderate", "amber"],
          ["Market context", "Available", "teal"],
          ["Contract data", "External source", "slate"],
        ].map(([k, v, tone]) => (
          <div key={k} className="flex items-center justify-between">
            <span className="text-xs text-white/60">{k}</span>
            <Chip tone={tone}>{v}</Chip>
          </div>
        ))}
      </div>
      <p className="rounded-xl border border-white/8 bg-white/[0.02] p-3 text-[11px] leading-relaxed text-white/45">
        Presale intelligence is analytical only and uses external market
        information where available. It is not investment advice.
      </p>
    </div>
  </div>
);

export const screenIndex = {
  analysis: { comp: AnalysisScreen, icon: Sparkle, label: "AI Analysis" },
  detail: { comp: AssetDetailScreen, icon: ChartLineUp, label: "Asset Detail" },
  monitoring: { comp: MonitoringScreen, icon: Bell, label: "Monitoring" },
  portfolio: { comp: PortfolioScreen, icon: Wallet, label: "Portfolio" },
  watchlist: { comp: WatchlistScreen, icon: Star, label: "Watchlist" },
  presale: { comp: PresaleScreen, icon: Rocket, label: "Presale" },
};
